import { useCallback, useState } from 'react';

import { Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';

import Button from 'domains/core/components/Button';
import { FolderBreadcrumbData } from 'domains/core/components/folders/types';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import InfoSnackbar from 'domains/core/components/Snackbars/InfoSnackbar';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar';
import useMoveItem from 'hooks/mutations/useMoveItem';
import { Folder, FolderLocation } from 'models/types';
import { isRootFolder, removeTypeFromId, rootFolderId } from 'utils/folders';

import CurentLocationText from './CurentLocationText';
import ModalItem from './ModalItem';
import ModalPreviewItem from './ModalPreviewItem';
import { useMoveItemConfig } from './useMoveItemConfig';

type Props = {
    activeItem: Folder;
    activeFolder: FolderBreadcrumbData;
    folderLocation: FolderLocation;
    handleModalClose: () => void;
    isOpen: boolean;
    refetch: () => void;
    resetActiveItem: () => void;
};

const MoveItemModal = ({
    activeItem,
    activeFolder,
    folderLocation,
    handleModalClose,
    isOpen,
    refetch,
    resetActiveItem,
}: Props) => {
    const [messages, setMessages] = useState({
        error: '',
        info: '',
        success: '',
    });
    const [locations, setLocations] = useState({
        future: null as Folder | null,
        path: [] as Folder[],
    });

    const { mutate: mutateMoveItem, isError, isLoading, isSuccess } = useMoveItem(folderLocation, {
        onError: ({ message }) => {
            setMessages((prev) => ({ ...prev, error: message }));
            handleModalCloseAndReset();
        },
        onSuccess: (response) => {
            setMessages((prev) => ({
                ...prev,
                success: `"${activeItem?.name}" successfully moved to "${locations.future?.name}."`,
            }));
            handleModalCloseAndReset();
            refetch();
            const { targetFolderId } = response;
            const contentViewParams = {
                newFolderId: targetFolderId === null ? rootFolderId : targetFolderId,
            };
            changeViewParams(contentViewParams);
        },
    });

    const moveItem = useCallback(
        (type: string) => {
            setMessages((prev) => ({
                ...prev,
                info: `Moving "${activeItem?.name}" to "${locations.future?.name}"`,
            }));
            const requestBody = {
                id: removeTypeFromId(activeItem?.id),
                type,
                ...(activeFolder.id !== rootFolderId && { currFolderId: activeFolder?.id }),
                ...(locations.future?.id !== rootFolderId && { targetFolderId: locations.future?.id }),
            };
            mutateMoveItem(requestBody);
        },
        [
            activeItem?.id,
            activeItem?.name,
            activeFolder.id,
            locations.future?.id,
            locations.future?.name,
            mutateMoveItem,
        ]
    );

    const currentPreviewedFolder = locations.path[locations.path.length - 1] ?? null;

    const moveItemConfigMap = useMoveItemConfig({
        activeItem,
        folderLocation,
        isOpen,
        moveItem,
        previewedLocation: currentPreviewedFolder,
    });

    const { changeViewParams, folderTypeValue, handleMoveItem, locationData, previewData, rootFolder, Icon } =
        moveItemConfigMap[folderLocation] || moveItemConfigMap.default;

    const CurrentLocationTextIcon = isRootFolder(activeFolder?.id) ? Icon : moveItemConfigMap.default.Icon;

    const ModalItems = () => {
        const getIsItemSelected = (item: Folder) => item?.id === locations.future?.id;
        const setFutureLocation = (future: Folder) => setLocations((prev) => ({ ...prev, future }));
        const drillIntoFolder = (folder: Folder) => setLocations((prev) => ({ ...prev, path: [...prev.path, folder] }));

        if (locations.path.length > 0) {
            return (
                <ModalPreviewItem
                    activeFolder={activeFolder}
                    data={previewData}
                    folderTypeValue={folderTypeValue}
                    getIsItemSelected={getIsItemSelected}
                    onDrillInto={drillIntoFolder}
                    onGoToPathIndex={(index: number) =>
                        setLocations((prev) => ({ ...prev, path: prev.path.slice(0, index + 1) }))
                    }
                    onGoToRoot={() => setLocations((prev) => ({ ...prev, path: [] }))}
                    onSelectFutureLocation={setFutureLocation}
                    path={locations.path}
                    rootFolder={rootFolder}
                />
            );
        }

        return (
            <Stack sx={{ padding: '1rem 1rem 0' }}>
                <ModalItem
                    key={rootFolder.id}
                    activeFolder={activeFolder}
                    DomainIcon={Icon}
                    isRowSelected={getIsItemSelected(rootFolder)}
                    rootFolder={rootFolder}
                    setFutureLocation={setFutureLocation}
                    setPreviewedLocation={drillIntoFolder}
                />
                {locationData?.map((location: Folder) => (
                    <ModalItem
                        key={location.id}
                        activeFolder={activeFolder}
                        isRowSelected={getIsItemSelected(location)}
                        row={location}
                        setFutureLocation={setFutureLocation}
                        setPreviewedLocation={drillIntoFolder}
                    />
                ))}
            </Stack>
        );
    };

    const handleModalCloseAndReset = useCallback(() => {
        setLocations({ future: null, path: [] });
        resetActiveItem();
        handleModalClose();
    }, [resetActiveItem, handleModalClose]);

    return (
        <>
            {isError && <ErrorSnackbar errorMessage={messages.error} />}
            {isLoading && <InfoSnackbar successMessage={messages.info} />}
            {isSuccess && <SuccessSnackbar successMessage={messages.success} />}
            <Dialog maxWidth="sm" fullWidth open={isOpen} onClose={handleModalCloseAndReset}>
                <DialogTitle>Move "{activeItem?.name}"</DialogTitle>
                <DialogContent sx={{ padding: 0 }}>
                    <CurentLocationText activeFolder={activeFolder} Icon={CurrentLocationTextIcon} />
                    {ModalItems()}
                </DialogContent>
                <DialogActions>
                    <Button variant="text" onClick={handleModalCloseAndReset}>
                        Cancel
                    </Button>
                    <Button
                        data-testid="move-button"
                        disabled={!locations.future}
                        variant="contained"
                        onClick={handleMoveItem}
                    >
                        Move
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MoveItemModal;
